import type { NuxtApp } from '#app'
import type { Country, Currency } from '@core/plugins/master-data/types'
import type { GQLPageMeta } from '@core/composables/use-page-meta/generated/page-meta'
import { MONEY_TRANSFER } from '@gifsa/language-utils/routes'
import { PageTypes } from '@core/constants/pages'
import { querySettings } from '@core/app/pages/query-settings'
import { queryPage } from '@core/app/pages/query-page'
import { getPageView } from '@core/analytics/pages'

import type { Corridor } from '@core/types/imt'
import type { GQLTransferUrls, GQLTransferUrlsVariables } from './generated/transfer-urls'
import QUERY_TRANSFER_URLS from './transfer-urls.graphql'
import exceptionsToIndex from './indexed-same-currency-transfer-pages.json'

const ONE_HOUR_IN_SECONDS = 3600
const ONE_DAY_IN_SECONDS = 3600 * 24

async function queryAlternateUrls(
  nuxtApp: NuxtApp,
  params: {
    countryFrom: Country
    countryTo: Country
    currencyFrom: Currency
    currencyTo: Currency
    canonical: boolean
  },
): Promise<GQLPageMeta['alternateLanguages']> {
  const { $apollo } = nuxtApp
  const { errors, data } = await $apollo.query<GQLTransferUrls, GQLTransferUrlsVariables>({
    query: QUERY_TRANSFER_URLS,
    variables: {
      countryFromIso: params.countryFrom.iso,
      countryToIso: params.countryTo.iso,
      currencyFromIso: params.currencyFrom.iso,
      currencyToIso: params.currencyTo.iso,
      canonical: params.canonical,
    },
    context: {
      cache: {
        type: 'revalidate',
        maxAge: ONE_HOUR_IN_SECONDS,
        revalidate: ONE_DAY_IN_SECONDS,
      },
    },
  })

  if (errors) {
    throw createGraphQLError(errors, 'Failed to load transfer page alternate urls')
  }

  if (!data.alternateUrls) {
    throw new NotFoundError('Invalid corridor. No alternate urls found')
  }

  const languages = getActiveLanguages()
  return languages
    .map((language) => {
      const slug =
        data.alternateUrls?.[language as keyof NonNullable<GQLTransferUrls['alternateUrls']>]
      return slug ? { language, slug } : null
    })
    .filter(isNotNull)
}

function shouldIndex(
  lang: string,
  countryFromIso: string,
  countryToIso: string,
  currencyFromIso: string,
  currencyToIso: string,
  canonicalUrls: GQLPageMeta['alternateLanguages'],
  alternateUrls: GQLPageMeta['alternateLanguages'],
) {
  const sameCurrency = currencyFromIso.toLowerCase() === currencyToIso.toLowerCase()
  if (sameCurrency) {
    const url = `${lang}/${countryFromIso}/${countryToIso}/${currencyFromIso}/${currencyToIso}`
    return exceptionsToIndex.includes(url.toLowerCase())
  }
  const canonicalUrl = canonicalUrls.find(({ language }) => language === lang)?.slug
  const currentUrl = alternateUrls.find(({ language }) => language === lang)?.slug
  return currentUrl === canonicalUrl
}

export default async function loadPageData() {
  const route = useRoute()
  const nuxtApp = useNuxtApp()
  const { $lang, $i18next } = nuxtApp

  const { data, error } = await useAsyncData(`pageData:${route.path}`, async () => {
    const { countryFrom, countryTo, currencyFrom, currencyTo, amount } = processIMTParams(
      route.params,
    )

    if (!currencyFrom || !currencyTo) {
      throw new NotFoundError(`Invalid currency params ${JSON.stringify(route.params)}`)
    }
    const params = { countryFrom, countryTo, currencyFrom, currencyTo, amount }

    const prefix = MONEY_TRANSFER.find((r) => r.lang === $lang)?.url
    if (!prefix) {
      throw new NotFoundError(`Unsupported language: ${$lang}`)
    }
    const slug = `${prefix}${countryFrom.slug}/${countryTo.slug}`
    const pattern = `${prefix}%COUNTRY_FROM_SLUG%/%COUNTRY_TO_SLUG%`

    const [
      { navigation, footer, footerContent },
      { body, landingContent, page },
      historicalData,
      alternateUrls,
      canonicalUrls,
    ] = await Promise.all([
      querySettings(nuxtApp),
      queryPage(nuxtApp, slug, pattern, 'monito', true),
      queryHistoricalStats(nuxtApp, params),
      queryAlternateUrls(nuxtApp, { ...params, canonical: false }),
      queryAlternateUrls(nuxtApp, { ...params, canonical: true }),
      $i18next.loadNamespaces(['provider_messages']),
    ])

    const corridor: Corridor = {
      ...params,
      defaultAmount: historicalData?.mostPopularAmount || 1000,
    }

    const noIndex = !shouldIndex(
      $lang,
      countryFrom.iso,
      countryTo.iso,
      currencyFrom.iso,
      currencyTo.iso,
      canonicalUrls,
      alternateUrls,
    )

    return {
      navigation,
      footer,
      body: [...body, ...(footerContent ?? [])],
      landingContent,
      page: { ...page, noIndex },
      corridor,
      alternateUrls,
      canonicalUrls,
      historicalData: {
        cheapestToBank: historicalData?.cheapestStdBank ?? null,
        cheapestToCash: historicalData?.cheapestStdCash ?? null,
        cheapestToCard: historicalData?.cheapestStdCard ?? null,
        cheapestToWallet: historicalData?.cheapestStdWallet ?? null,
      },
    }
  })

  if (!data.value) {
    throw createAsyncDataError(error)
  }

  if (import.meta.client) {
    const { page, corridor } = data.value
    await nuxtApp.$track(
      getPageView(route, {
        type: PageTypes.TRANSFER,
        page: { tags: page.tags || [], context: page.context },
        corridor,
      }),
    )
  }

  return data.value
}
